'use client';

import { useEffect, useRef, useLayoutEffect } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

// Register ScrollTrigger plugin
if (typeof window !== 'undefined') {
  gsap.registerPlugin(ScrollTrigger);
}

const useIsomorphicLayoutEffect =
  typeof window !== 'undefined' ? useLayoutEffect : useEffect;

/**
 * Hook for scroll-triggered animations
 * @param {Object} options - ScrollTrigger options
 * @returns {Object} ref
 */
export function useScrollTrigger(options = {}) {
  const {
    animation = { opacity: 1, y: 0 },
    from = { opacity: 0, y: 50 },
    trigger = null,
    start = 'top 85%',
    end = 'bottom 15%',
    toggleActions = 'play none none reverse',
    scrub = false,
    markers = false,
    onEnter,
    onLeave,
    onEnterBack,
    onLeaveBack,
    deps = [],
  } = options;

  const ref = useRef(null);

  useIsomorphicLayoutEffect(() => {
    if (!ref.current) return;

    const ctx = gsap.context(() => {
      gsap.fromTo(ref.current, from, {
        ...animation,
        scrollTrigger: {
          trigger: trigger || ref.current,
          start,
          end,
          toggleActions,
          scrub,
          markers,
          onEnter,
          onLeave,
          onEnterBack,
          onLeaveBack,
        },
      });
    }, ref);

    return () => ctx.revert();
  }, deps);

  return ref;
}

/**
 * Hook for parallax scroll effect
 * @param {Object} options - Parallax options
 * @returns {Object} ref
 */
export function useParallax(options = {}) {
  const {
    speed = 0.5,
    direction = 'vertical',
    deps = [],
  } = options;

  const ref = useRef(null);

  useIsomorphicLayoutEffect(() => {
    if (!ref.current) return;

    const movement = speed * 100;

    const ctx = gsap.context(() => {
      gsap.to(ref.current, {
        y: direction === 'vertical' ? movement : 0,
        x: direction === 'horizontal' ? movement : 0,
        ease: 'none',
        scrollTrigger: {
          trigger: ref.current,
          start: 'top bottom',
          end: 'bottom top',
          scrub: true,
        },
      });
    }, ref);

    return () => ctx.revert();
  }, deps);

  return ref;
}

/**
 * Hook for pinned sections
 * @param {Object} options - Pin options
 * @returns {Object} ref
 */
export function usePin(options = {}) {
  const {
    start = 'top top',
    end = '+=100%',
    pinSpacing = true,
    deps = [],
  } = options;

  const ref = useRef(null);

  useIsomorphicLayoutEffect(() => {
    if (!ref.current) return;

    const trigger = ScrollTrigger.create({
      trigger: ref.current,
      start,
      end,
      pin: true,
      pinSpacing,
    });

    return () => trigger.kill();
  }, deps);

  return ref;
}

export default useScrollTrigger;
